import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, CheckCheck, Inbox, Lock, Volume2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useNotifications } from "@/context/NotificationContext";
import { relTime } from "@/components/admin/NotificationBell";

const TYPES = [
  { value: "", label: "Semua Tipe" },
  { value: "ORDER_CREATED", label: "Pesanan Baru" },
  { value: "PAYMENT_SUCCESS", label: "Pembayaran" },
];

function PermissionBanner({ prefs, entitlements, savePrefs }) {
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("notif_perm_later") === "1");
  if (perm === "unsupported" || !entitlements.browser || dismissed || perm === "granted") return null;
  if (perm === "denied") {
    return (
      <Card className="p-4 rounded-2xl border-amber-200 bg-amber-50 text-amber-800 text-sm" data-testid="notif-permission-denied">
        Notifikasi browser <b>diblokir</b> oleh browser Anda. Izinkan notifikasi untuk situs ini lewat pengaturan browser (ikon gembok di address bar), lalu muat ulang halaman.
      </Card>
    );
  }
  const activate = async () => {
    const res = await Notification.requestPermission();
    setPerm(res);
    if (res === "granted") {
      try { await savePrefs({ browser_notification_enabled: true }); toast.success("Notifikasi browser aktif."); }
      catch (e) { toast.error(formatApiError(e)); }
    } else if (res === "denied") {
      toast.error("Izin notifikasi ditolak browser.");
    }
  };
  return (
    <Card className="p-4 rounded-2xl flex flex-wrap items-center gap-3 bg-[color:var(--brand-surface)] border-[color:var(--brand-border)]" data-testid="notif-permission-banner">
      <BellRing size={18} className="brand-text-primary shrink-0" />
      <span className="text-sm flex-1 min-w-[200px]">Aktifkan notifikasi browser agar Anda tidak melewatkan pesanan baru.</span>
      <div className="flex gap-2">
        <Button size="sm" onClick={activate} className="brand-btn-primary rounded-xl" data-testid="notif-permission-activate">Aktifkan</Button>
        <Button size="sm" variant="outline" className="rounded-xl" data-testid="notif-permission-later"
          onClick={() => { sessionStorage.setItem("notif_perm_later", "1"); setDismissed(true); }}>Nanti</Button>
      </div>
    </Card>
  );
}

function PrefRow({ label, desc, checked, onChange, testId, locked, lockNote }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b last:border-0 border-[color:var(--brand-border)]">
      <div>
        <Label className="text-sm font-bold flex items-center gap-1.5">{label} {locked && <Lock size={12} className="text-gray-400" />}</Label>
        <p className="text-xs text-[color:var(--brand-muted)]">{locked ? lockNote : desc}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={locked} data-testid={testId} />
    </div>
  );
}

export default function NotificationsPage() {
  const { prefs, entitlements, savePrefs, markRead, markAllRead, unreadCount, refresh } = useNotifications();
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 15 });
  const [loading, setLoading] = useState(true);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/notifications", { params: { page, limit: 15, unread_only: unreadOnly, type: type || undefined } })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, unreadOnly, type]);

  useEffect(() => { load(); }, [load]);

  const togglePref = async (key, value) => {
    try { await savePrefs({ [key]: value }); toast.success("Preferensi tersimpan."); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const onClickItem = async (n) => {
    if (!n.is_read) { await markRead(n.id).catch(() => {}); }
    navigate(n.action_url || "/admin/notifications");
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / 15));

  return (
    <AdminLayout title="Notifikasi">
      <div className="grid lg:grid-cols-[1fr_320px] gap-5 items-start" data-testid="notifications-page">
        <div className="space-y-4">
          <PermissionBanner prefs={prefs} entitlements={entitlements} savePrefs={savePrefs} />
          <Card className="rounded-2xl overflow-hidden bg-[color:var(--brand-surface)] border-[color:var(--brand-border)]">
            <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-[color:var(--brand-border)]">
              <button onClick={() => { setUnreadOnly(false); setPage(1); }} data-testid="filter-all"
                className={`text-xs font-bold px-3 py-1.5 rounded-full ${!unreadOnly ? "brand-bg-primary text-white" : "border border-[color:var(--brand-border)]"}`}>Semua</button>
              <button onClick={() => { setUnreadOnly(true); setPage(1); }} data-testid="filter-unread"
                className={`text-xs font-bold px-3 py-1.5 rounded-full ${unreadOnly ? "brand-bg-primary text-white" : "border border-[color:var(--brand-border)]"}`}>
                Belum dibaca {unreadCount > 0 && `(${unreadCount})`}
              </button>
              <select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} data-testid="filter-type"
                className="text-xs font-bold px-3 py-1.5 rounded-full border border-[color:var(--brand-border)] bg-transparent">
                {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              {unreadCount > 0 && (
                <button onClick={async () => { await markAllRead(); load(); }} data-testid="page-mark-all-read"
                  className="ml-auto text-xs font-bold brand-text-primary flex items-center gap-1"><CheckCheck size={13} /> Tandai semua dibaca</button>
              )}
            </div>
            {loading ? (
              <div className="p-4 space-y-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-14 rounded-xl bg-black/5 animate-pulse" />)}</div>
            ) : data.items.length === 0 ? (
              <div className="p-12 text-center text-sm text-[color:var(--brand-muted)]" data-testid="notifications-empty">
                <Inbox size={32} className="mx-auto mb-3 opacity-40" />
                {unreadOnly ? "Tidak ada notifikasi belum dibaca." : "Belum ada notifikasi. Pesanan baru akan muncul di sini."}
              </div>
            ) : data.items.map((n) => (
              <button key={n.id} onClick={() => onClickItem(n)} data-testid={`notifications-row-${n.id}`}
                className={`w-full text-left px-4 py-3.5 border-b last:border-0 border-[color:var(--brand-border)] hover:brand-soft-bg transition-colors flex gap-3 ${!n.is_read ? "bg-[color:var(--brand-background)]" : ""}`}>
                <span className={`mt-1 w-2.5 h-2.5 rounded-full shrink-0 ${!n.is_read ? "brand-bg-primary" : "bg-transparent"}`} />
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-bold truncate">{n.title}</span>
                    <span className="text-[10px] uppercase font-bold text-[color:var(--brand-muted)]">{n.type === "ORDER_CREATED" ? "Pesanan" : n.type === "PAYMENT_SUCCESS" ? "Pembayaran" : n.type}</span>
                  </span>
                  <span className="block text-xs text-[color:var(--brand-muted)]">{n.message}</span>
                  <span className="block text-[10px] text-[color:var(--brand-muted)] opacity-70 mt-0.5">{relTime(n.created_at)}</span>
                </span>
              </button>
            ))}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-[color:var(--brand-border)] text-xs">
                <Button size="sm" variant="outline" className="rounded-xl" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="notif-prev-page">Sebelumnya</Button>
                <span className="text-[color:var(--brand-muted)]">Hal {data.page} / {totalPages} · {data.total} notifikasi</span>
                <Button size="sm" variant="outline" className="rounded-xl" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="notif-next-page">Berikutnya</Button>
              </div>
            )}
          </Card>
        </div>

        <Card className="rounded-2xl p-4 bg-[color:var(--brand-surface)] border-[color:var(--brand-border)]" data-testid="notification-settings">
          <h3 className="font-bold text-sm mb-1 flex items-center gap-2"><Bell size={14} /> Preferensi Notifikasi</h3>
          <p className="text-xs text-[color:var(--brand-muted)] mb-2">Notifikasi in-app selalu aktif. Preferensi disimpan per akun Anda.</p>
          {!prefs ? (
            <div className="h-24 rounded-xl bg-black/5 animate-pulse" />
          ) : (
            <>
              <PrefRow label={<><Volume2 size={13} className="inline mr-1" />Suara Notifikasi</>} desc="Bunyi singkat saat notifikasi baru masuk."
                checked={prefs.sound_enabled} onChange={(v) => togglePref("sound_enabled", v)} testId="pref-sound"
                locked={!entitlements.sound} lockNote="Tidak tersedia di paket Anda." />
              <PrefRow label="Notifikasi Browser" desc="Muncul saat dashboard sedang aktif (bukan background push)."
                checked={prefs.browser_notification_enabled} onChange={(v) => togglePref("browser_notification_enabled", v)} testId="pref-browser"
                locked={!entitlements.browser} lockNote="Tersedia mulai paket GROWTH. Upgrade untuk mengaktifkan." />
              <PrefRow label="Pesanan Baru" desc="Notifikasi saat pesanan baru masuk."
                checked={prefs.order_created_enabled} onChange={(v) => { togglePref("order_created_enabled", v); setTimeout(() => { load(); refresh(); }, 300); }} testId="pref-order-created" />
              <PrefRow label="Pembayaran Berhasil" desc="Notifikasi saat pembayaran diterima."
                checked={prefs.payment_success_enabled} onChange={(v) => { togglePref("payment_success_enabled", v); setTimeout(() => { load(); refresh(); }, 300); }} testId="pref-payment-success" />
            </>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
}
